import { localDateKey } from '../../../utils/datetime';
// Merge the local draft queue onto the server snapshot so the grid + modals
// render unpublished edits live. Every produced/edited record is tagged with
// `__draft: true` so the UI can mark it. This is a PURE function — it deep-
// clones its inputs and never mutates the server data.
//
// The shape of each `change` matches the Redux slice:
//   { id, type, payload, label, dedupKey? }

const clone = (x) => (x ? JSON.parse(JSON.stringify(x)) : x);

// Build a tz-naive local timestamp for a given day + HH:MM (no trailing Z) so
// localDateKey() resolves it back to the same calendar day it was created for.
const toIso = (dateStr, t) => `${dateStr}T${t || '00:00'}:00`;

const sameDate = (shift, dateStr) => {
    if (!shift) return false;
    if (shift.shift_date === dateStr) return true;
    if (shift.start_time) {
        try { return localDateKey(shift.start_time) === dateStr; } catch (_) { /* noop */ }
    }
    return false;
};

const upsertShift = (emp, dateStr, draftShift) => {
    if (!Array.isArray(emp.shifts)) emp.shifts = [];
    const idx = emp.shifts.findIndex((s) => sameDate(s, dateStr));
    if (idx >= 0) emp.shifts[idx] = draftShift;
    else emp.shifts.push(draftShift);
};

export function applyDraft(serverGroups, serverEmployees, changes) {
    const groups = clone(serverGroups) || [];
    const employees = clone(serverEmployees) || [];
    if (!Array.isArray(changes) || changes.length === 0) {
        return { groups, employees };
    }

    const empById = new Map(employees.map((e) => [e.id, e]));
    const groupById = new Map(groups.map((g) => [g.id, g]));

    for (const c of changes) {
        const p = c.payload || {};
        switch (c.type) {
            case 'cover': {
                const emp = empById.get(Number(p.employee_id));
                if (!emp) break;
                upsertShift(emp, p.date, {
                    id: c.id,
                    shift_date: p.date,
                    start_time: toIso(p.date, p.start_time),
                    end_time: toIso(p.date, p.end_time),
                    status: 'scheduled',
                    break_time_minutes: 0,
                    worked_minutes: 0,
                    session_minutes: 0,
                    notes: p.note || null,
                    __draft: true,
                });
                break;
            }
            case 'reassign': {
                for (const a of p.assignments || []) {
                    const emp = empById.get(Number(a.user_id));
                    if (emp && emp.rosterDetails) {
                        emp.rosterDetails.group_id = a.group_id;
                        emp.rosterDetails.__draft = true;
                    }
                }
                break;
            }
            case 'rename': {
                const grp = groupById.get(Number(p.teamId));
                if (grp) {
                    grp.name = p.name;
                    grp.manager_id = p.manager_id;
                    if (p.manager_name !== undefined) grp.manager_name = p.manager_name;
                    grp.__draft = true;
                }
                break;
            }
            case 'removeDay': {
                const emp = empById.get(Number(p.userId));
                if (emp && emp.rosterDetails) {
                    let days = emp.rosterDetails.working_days;
                    if (typeof days === 'string') { try { days = JSON.parse(days); } catch (_) { days = []; } }
                    if (Array.isArray(days)) {
                        emp.rosterDetails.working_days = days.filter((d) => d !== p.dayName);
                        emp.rosterDetails.__draft = true;
                    }
                }
                break;
            }
            case 'review': {
                const emp = empById.get(Number(p.employeeId));
                if (!emp) break;
                const startIso = toIso(p.date, p.clockIn);
                const endIso = toIso(p.date, p.clockOut);
                const worked = Math.max(0, Math.round((new Date(endIso) - new Date(startIso)) / 60000));
                // Carry the published change log forward and preview the entry
                // the API will append on publish, so the edit is visible now.
                const prev = (emp.shifts || []).find((s) => sameDate(s, p.date));
                const hoursChangeLog = [
                    ...(Array.isArray(prev?.hours_change_log) ? prev.hours_change_log : []),
                    {
                        at: new Date().toISOString(),
                        by_name: null,
                        from: prev?.start_time ? { start: prev.start_time, end: prev.end_time || null } : null,
                        to: { start: startIso, end: endIso },
                        note: p.note || null,
                        __draft: true,
                    },
                ];
                upsertShift(emp, p.date, {
                    id: c.id,
                    shift_date: p.date,
                    start_time: startIso,
                    end_time: endIso,
                    status: 'completed',
                    break_time_minutes: 0,
                    worked_minutes: worked,
                    session_minutes: worked,
                    notes: p.note || null,
                    // Saving the review IS the approval — mirror what the API
                    // will stamp on publish so the cell drops out of the review
                    // queue straight away instead of still showing "Review".
                    hours_approved_at: new Date().toISOString(),
                    hours_change_log: hoursChangeLog,
                    __draft: true,
                });
                break;
            }
            case 'absence': {
                const emp = empById.get(Number(p.employee_id));
                if (!emp) break;
                if (!Array.isArray(emp.unavailability)) emp.unavailability = [];
                emp.unavailability = [
                    { id: c.id, start_date: p.start_date, end_date: p.end_date, reason: p.reason, leave_type: p.leave_type ?? null, notes: p.notes, __draft: true },
                    ...emp.unavailability,
                ];
                break;
            }
            case 'removeAbsence': {
                const emp = empById.get(Number(p.employee_id));
                if (emp && Array.isArray(emp.unavailability)) {
                    emp.unavailability = emp.unavailability.filter((u) => String(u.id) !== String(p.id));
                }
                break;
            }
            default:
                break;
        }
    }

    return { groups, employees };
}

export default applyDraft;
